import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GenesysCloudPlatformApiService } from '../../common/services/genesys-cloud-platform-api.service';
import { GenesysCloudUser } from '../models/user.model';

export interface UserSearchResponse {
  results: GenesysCloudUser[];
  pageCount: number;
  pageNumber: number;
  total: number;
}

export interface UserSearchRequest {
  query: UserSearchCriteria[];
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
  pageSize?: number;
  pageNumber?: number;
}

interface UserSearchCriteria {
  fields: string[];
  value: string;
  type: 'EXACT' | 'STARTS_WITH' | 'CONTAINS' | 'REGEX' | 'TERM' | 'TERMS' | 'REQUIRED_FIELDS' | 'MATCH_ALL' | 'QUERY_STRING';
}

export interface UserQueryResponse {
  selfUri: string;
  nextUri: string;
  lastUri: string;
  pageCount: number;
  pageNumber: number;
  total: number;
  entities: GenesysCloudUser[];
}

@Injectable({
  providedIn: 'root'
})
export class UserApiService {
  static readonly CurrentUserPath = 'api/v2/users/me';
  static readonly SearchUsersPath = 'api/v2/users/search';
  static readonly GetUsersPath = 'api/v2/users';

  constructor(private readonly api: GenesysCloudPlatformApiService) {
  }

  getUserById$(userId: string): Observable<GenesysCloudUser> {
    return this.api.get$<GenesysCloudUser>(`${UserApiService.GetUsersPath}/${userId}`);
  }

  getCurrentUser$(): Observable<GenesysCloudUser> {
    return this.api.get$<GenesysCloudUser>(UserApiService.CurrentUserPath, {
      params: {
        expand: ['presence', 'orgproducts', 'authorization']
      }
    });
  }

  getUsers$(pageSize: number, pageNumber: number): Observable<UserSearchResponse> {
    return this.api.get$<UserQueryResponse>(UserApiService.GetUsersPath, {
      params: {
        expand: 'presence',
        pageSize: `${pageSize}`,
        pageNumber: `${pageNumber}`
      }
    }).pipe(
      map(response => this.mapToUserSearchResponse(response))
    );
  }

  searchUsers$(searchTerm: string, pageSize: number, pageNumber: number): Observable<UserSearchResponse> {
    return this.api.post$<UserSearchResponse>(UserApiService.SearchUsersPath, this.getSearchRequest(searchTerm, pageSize, pageNumber));
  }

  private mapToUserSearchResponse(userQueryResponse: UserQueryResponse): UserSearchResponse {
    return {
      results: userQueryResponse.entities,
      pageCount: userQueryResponse.pageCount,
      pageNumber: userQueryResponse.pageNumber,
      total: userQueryResponse.total
    };
  }

  private getSearchRequest(searchTerm: string, pageSize: number, pageNumber: number): UserSearchRequest {
    return {
      query: [{
        fields: ['name'],
        value: searchTerm,
        type: 'CONTAINS'
      }],
      sortBy: 'name',
      sortOrder: 'ASC',
      expand: ['presence'],
      pageSize,
      pageNumber
    };
  }
}

